import React, { useEffect } from "react";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import {
  useAllUserOrgPreferences,
  useUpdateUserOrgPreferences,
} from "../../hooks/useUserOrgPreferences";
import { UserOrgPreferencesDto } from "../../../common/service/server/dto/UserDto";
import { Button, Table, TextInput, Toggle } from "@truevault/aquilo";
import { PageHeader } from "../../components/layout/header/Header";
import { PageHeaderBack } from "../../components/layout/header/PageHeaderBack";
import { Routes } from "../../root/routes";
import { Form } from "react-final-form";
import { useSelfOrganizationUser } from "../../hooks/useOrganization";

interface OrgPreference {
  weeklyDigestEmail: boolean;
  newRequestEmail?: boolean;
  isDisabled: boolean;
}

export const UserProfilePage = () => {
  const [allUserOrgPreferences = [], ,] = useAllUserOrgPreferences();
  const { updateUserOrgPreferences } = useUpdateUserOrgPreferences();
  const [orgPreferences, setOrgPreferences] = React.useState<Record<string, OrgPreference>>({});
  const selfOrgUser = useSelfOrganizationUser();

  useEffect(() => {
    setOrgPreferences(
      Object.fromEntries(
        allUserOrgPreferences.map((pref) => [
          pref.organizationId,
          {
            weeklyDigestEmail: pref.weeklyDigestEmail,
            isDisabled: false,
            newRequestEmail: pref.newRequestEmail,
          },
        ]),
      ),
    );
  }, [allUserOrgPreferences]);

  const handleToggleChange = async (
    orgId: string,
    preferences: { weeklyDigestEmail?: boolean; newRequestEmail?: boolean },
  ) => {
    setOrgPreferences((prev) => ({
      ...prev,
      [orgId]: {
        weeklyDigestEmail: preferences.weeklyDigestEmail ?? prev[orgId].weeklyDigestEmail,
        newRequestEmail: preferences.newRequestEmail ?? prev[orgId].newRequestEmail,
        isDisabled: true,
      },
    }));

    try {
      await updateUserOrgPreferences({
        organizationId: orgId,
        preferences: {
          weeklyDigestEmail:
            preferences.weeklyDigestEmail ?? orgPreferences[orgId].weeklyDigestEmail,
          newRequestEmail: preferences.newRequestEmail ?? orgPreferences[orgId].newRequestEmail,
        },
      });
    } finally {
      setOrgPreferences((prev) => ({
        ...prev,
        [orgId]: { ...prev[orgId], isDisabled: false },
      }));
    }
  };

  const columns = [
    {
      key: "organizationName",
      header: "Organization Name",
      className: "px-3 py-3.5 pl-4 pr-3 font-medium text-gray-900 sm:pl-0",
    },
    {
      key: "weeklyDigestEmail",
      header: "Weekly Digest",
      className: "px-3 py-3.5 pl-4 pr-3 text-center",
      render: (orgPref: UserOrgPreferencesDto) => (
        <div className="flex justify-center">
          {" "}
          {/* Center the toggle */}
          <Toggle
            checked={orgPreferences[orgPref.organizationId]?.weeklyDigestEmail ?? false}
            disabled={orgPreferences[orgPref.organizationId]?.isDisabled ?? false}
            onChange={(checked) =>
              handleToggleChange(orgPref.organizationId, { weeklyDigestEmail: checked })
            }
            label={`Toggle status for ${orgPref.organizationName}`}
          />
        </div>
      ),
    },
    {
      key: "newRequestEmail",
      header: "New Privacy Requests",
      className: "px-3 py-3.5 pl-4 pr-3 text-center",
      render: (orgPref: UserOrgPreferencesDto) => (
        <div className="flex justify-center">
          {" "}
          {/* Center the toggle */}
          <Toggle
            checked={orgPreferences[orgPref.organizationId]?.newRequestEmail ?? false}
            disabled={orgPreferences[orgPref.organizationId]?.isDisabled ?? false}
            onChange={(checked) =>
              handleToggleChange(orgPref.organizationId, { newRequestEmail: checked })
            }
            label={`Toggle status for ${orgPref.organizationName}`}
          />
        </div>
      ),
    },
  ];

  const tableArgs = {
    data: [...allUserOrgPreferences].sort((a, b) =>
      a.organizationName.localeCompare(b.organizationName),
    ),
    keyExtractor: (pref: UserOrgPreferencesDto) => pref.organizationId,
    heading: "Email Notifications",
    description:
      "We'll always let you know about product changes and legal updates. You can configure other types of email notifications below.",
  };

  const emailComponentArgs = {
    field: "email",
    label: "Email Address",
    placeholder: `${selfOrgUser.email}`,
    disabled: true,
  };

  const backRoute = selfOrgUser?.role === "HR" ? Routes.hrCenter.root : Routes.dashboard.root;
  const backTitle = selfOrgUser?.role === "HR" ? "Back to HR Center" : "Back to Dashboard";

  return (
    <StandardPageWrapper fullWidth navContent={false} includeNav={false}>
      <div style={{ maxWidth: "1060px" }} className="mx-auto">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <img src="/assets/images/truevault-logo-print.png" width="150px" height="33px" />
        </div>
        <PageHeader
          titleHeaderContent={<PageHeaderBack to={backRoute}>{backTitle}</PageHeaderBack>}
          titleContent="Your Profile"
        />
        <div className="tw-boundary">
          <div
            className={`rounded-[10px] p-6 w-full`}
            style={{ boxShadow: "0px 2px 12px 0px rgba(0, 0, 0, 0.12)" }}
          >
            <div className="px-4 sm:px-6 lg:px-8">
              <Form
                onSubmit={() => {}}
                render={() => (
                  <div style={{ width: "400px" }} className="p-12">
                    <form>
                      <TextInput {...emailComponentArgs} />
                      <p className="mt-2 text-sm text-gray-500">
                        TrueVault notifications will be sent to this email
                      </p>
                    </form>
                  </div>
                )}
              />
            </div>
            <div className="px-4 sm:px-6 lg:px-8 mt-8">
              <Table {...tableArgs} columns={columns} />
            </div>
            <div className="flex justify-end mt-4 px-4 sm:px-6 lg:px-8">
              <Button size="s" color="primary" onClick={() => (window.location.href = backRoute)}>
                {"Done with Profile"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </StandardPageWrapper>
  );
};
